import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { isChatStatusBusy } from "../Chat/helpers";
import useChatStore from "../Chat/store/useStore";
import { ChatMode } from "../Chat/types";
import { AssistantPanelView } from "../Layout/types";
import useAssistantPanelStore from "../Layout/useStore";
import useIsAssistantPageMode from "./useIsAssistantPageMode";

const useAssistantPanelSendPrompt = () => {
  const isAssistantPageMode = useIsAssistantPageMode();
  const { setIsOpen, switchView, isOpen } = useAssistantPanelStore(
    useShallow((state) => ({
      setIsOpen: state.setIsOpen,
      switchView: state.switchView,
      isOpen: state.isOpen,
    }))
  );

  const { appendChat, resetChat, setMode } = useChatStore(
    useShallow((state) => ({
      appendChat: state.appendChat,
      resetChat: state.resetChat,
      setMode: state.setMode,
    }))
  );

  const openPrompt = useCallback(
    (prompt: string, { newChat = false, mode }: { newChat?: boolean; mode?: ChatMode } = {}) => {
      if (!isAssistantPageMode) {
        setIsOpen(true);
        switchView(AssistantPanelView.CHAT);
      }

      // Ask only drafts a prompt; it must not throw away an answer still being written.
      if (newChat && !isChatStatusBusy(useChatStore.getState().chat?.status)) {
        resetChat();
      }

      if (mode) {
        setMode(mode);
      }

      appendChat(prompt);
    },
    [isAssistantPageMode, setIsOpen, switchView, resetChat, setMode, appendChat]
  );

  return { openPrompt, isChatOpen: isOpen || isAssistantPageMode };
};

export default useAssistantPanelSendPrompt;
